import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { DataTable } from "@outpave/ds";
import type { DataColumn, DateRangeValue } from "@outpave/ds";
import dayjs from "utils/dayjs";
import { GET_TRANSACTION_ROWS } from "graphql/transaction";
import { DEFAULT_PAGINATION_SIZE } from "components/common/Constants";
import { formatCashFull } from "utils/formatCash";
import { RenderTransactionStatus } from "components/common/RenderTransactionStatus";
import { RenderTransactionTypeChip } from "components/common/RenderTransactionType";
import TransactionStatus from "enums/TransactionStatus";
import useInfiniteScroll from "hooks/useInfiniteScroll";
import { useAdminTools } from "hooks/useAdminTools";
import { REIMBURSEMENT_TYPES } from "utils/transactions/transactionGroups";
import { amountQuery } from "utils/transactions/amountQuery";
import { resolveDateRange } from "utils/dateRangeFilter";
import type { AmountRange } from "components/common/AmountRangeChip";
import {
  mapReimbursementNode,
  type ReimbursementNode,
  type ReimbursementRow,
} from "utils/reimbursements/reimbursementRows";
import UserAvatar from "components/common/users/UserAvatar";

export type ReimbursementFilterCriteria = {
  status?: TransactionStatus[] | null;
  searchTerm?: string;
  /** A user id. The query takes one, so the Employee filter is single select. */
  cardHolder?: string | null;
  amountRange?: AmountRange;
  dateRange?: DateRangeValue;
};

type ReimbursementsTableProps = {
  filterCriteria: ReimbursementFilterCriteria;
  handleOpenDrawer: (transactionId: string) => void;
  refreshReimbursements?: number;
  /** Only the viewer's own reimbursements, as every /my list must be. */
  mine?: boolean;
};

type ReimbursementConnection = Record<
  string,
  { edges: { node: ReimbursementNode }[] } | undefined
>;

const extractReimbursements = (data: ReimbursementConnection) => {
  const firstKey = Object.keys(data)[0];
  return (data[firstKey]?.edges ?? []).map((item) =>
    mapReimbursementNode(item.node),
  );
};

export const reimbursementsTableVariables = (
  filterCriteria: ReimbursementFilterCriteria,
  { mine, adminColumns }: { mine: boolean; adminColumns: boolean },
) => ({
  first: DEFAULT_PAGINATION_SIZE,
  after: undefined,
  type: REIMBURSEMENT_TYPES,
  transactionStatus: filterCriteria.status,
  searchTerm: filterCriteria.searchTerm,
  cardHolder: filterCriteria.cardHolder,
  amount: amountQuery(filterCriteria.amountRange),
  ...resolveDateRange(filterCriteria.dateRange),
  ...(mine && { mine: true }),
  withWorkflow: true,
  // The Employee column is the only reader of the per-row user lookup.
  withAdminColumns: adminColumns,
});

/** Employee is Manager and above, off on /my, as the transactions table's Purchaser is. */
export const reimbursementsTableColumns = (
  t: ReturnType<typeof useTranslation>["t"],
  { adminColumns }: { adminColumns: boolean },
): DataColumn<ReimbursementRow>[] => [
  {
    id: "date",
    header: t("Date"),
    width: 0,
    cell: (row) => (
      <Typography variant="body2" component="span" noWrap>
        {dayjs(row.date).format("MMM D, YYYY")}
      </Typography>
    ),
  },
  ...(adminColumns
    ? ([
        {
          id: "employee",
          header: t("Employee"),
          maxWidth: 200,
          cell: (row) => ({
            avatar: <UserAvatar size="sm" user={row.user} />,
            content: row.userName,
          }),
        },
      ] as DataColumn<ReimbursementRow>[])
    : []),

  {
    id: "description",
    header: t("Description"),
    maxWidth: 320,
    cell: (row) => row.description,
  },
  {
    id: "type",
    header: t("Type"),
    width: 0,
    cell: (row) => <RenderTransactionTypeChip transactionType={row.type} />,
  },
  {
    id: "amount",
    header: t("Amount"),
    align: "center",
    width: 140,
    cell: (row) => ({
      content: formatCashFull(row.amount, row.currency),
      align: "right",
    }),
  },
  {
    id: "status",
    header: t("Status"),
    align: "center",
    width: 0,
    cell: (row) => (
      <RenderTransactionStatus
        transactionStatus={row.status}
        workflowStatus={row.workflowStatus}
        transactionType={row.type}
        smallPill
      />
    ),
  },
];

const ReimbursementsTable: React.FC<ReimbursementsTableProps> = ({
  filterCriteria,
  handleOpenDrawer,
  refreshReimbursements,
  mine = false,
}) => {
  const { t } = useTranslation();
  const adminColumns = useAdminTools();

  // Memoised rather than held in state: `useInfiniteScroll` stringifies this
  // object to decide when to reset, so a fresh object every render refetches.
  const initialVariables = useMemo(
    () => reimbursementsTableVariables(filterCriteria, { mine, adminColumns }),
    [filterCriteria, mine, adminColumns],
  );

  const { dataArray, displayLoading, handleScroll, isInfiniteScrollFetching } =
    useInfiniteScroll({
      query: GET_TRANSACTION_ROWS,
      initialVariables,
      extractData: extractReimbursements,
      refetchIndex: refreshReimbursements,
    });

  const columns = useMemo(
    () => reimbursementsTableColumns(t, { adminColumns }),
    [t, adminColumns],
  );

  return (
    <Box sx={{ position: "relative", height: "100%" }}>
      <DataTable<ReimbursementRow>
        columns={columns}
        rows={dataArray}
        getRowKey={(row) => row.id}
        loading={displayLoading}
        isFetchingMore={isInfiniteScrollFetching}
        onRowClick={(row) => handleOpenDrawer(row.id)}
        onScroll={handleScroll}
        emptyState={t("No reimbursements found")}
        loadingMoreText={t("Loading more reimbursements")}
        stickyHeader
      />
    </Box>
  );
};

export default ReimbursementsTable;
